import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Professional, accessible pagination component.
 *
 * Props:
 *  - currentPage: 1-based page index
 *  - totalPages
 *  - onPageChange(page)
 *  - totalItems / pageSize (for the "Showing X–Y of Z" label)
 *  - pageSize, onPageSizeChange (optional selector — pass to hide)
 */
const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
  totalItems,
  pageSize,
  onPageSizeChange,
  pageSizeOptions = [6, 12, 24],
}) => {
  if (totalPages <= 1 && !onPageSizeChange) return null;

  const start = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const end = Math.min(currentPage * pageSize, totalItems);

  // Build compact page list: 1 … (current-1) (current) (current+1) … last
  const pages = [];
  const add = (p) => pages.push(p);
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) add(i);
  } else {
    add(1);
    if (currentPage > 3) add("…");
    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) add(i);
    if (currentPage < totalPages - 2) add("…");
    add(totalPages);
  }

  return (
    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mt-6 pt-4 border-t border-slate-200">
      <div className="flex items-center gap-3 text-xs text-slate-500">
        {totalItems !== undefined && (
          <span>
            Showing <span className="font-semibold text-slate-700">{start}</span>–
            <span className="font-semibold text-slate-700">{end}</span> of{" "}
            <span className="font-semibold text-slate-700">{totalItems}</span>
          </span>
        )}
        {onPageSizeChange && (
          <span className="flex items-center gap-1.5">
            <span className="hidden sm:inline">·</span>
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange(parseInt(e.target.value, 10))}
              className="border border-slate-200 rounded px-1.5 py-0.5 text-xs font-medium text-slate-700 bg-white hover:border-slate-300 focus:border-orange-500 outline-none"
            >
              {pageSizeOptions.map((n) => (
                <option key={n} value={n}>
                  {n}/page
                </option>
              ))}
            </select>
          </span>
        )}
      </div>

      {totalPages > 1 && (
        <nav className="flex items-center gap-1" aria-label="Pagination">
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="w-8 h-8 flex items-center justify-center rounded-md border border-slate-200 text-slate-600 hover:border-slate-300 hover:text-slate-900 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            aria-label="Previous page"
          >
            <ChevronLeft size={14} />
          </button>
          {pages.map((p, i) =>
            p === "…" ? (
              <span key={`e${i}`} className="w-8 h-8 flex items-center justify-center text-slate-400 text-xs">
                …
              </span>
            ) : (
              <button
                key={p}
                onClick={() => onPageChange(p)}
                aria-current={p === currentPage ? "page" : undefined}
                className={`min-w-[2rem] h-8 px-2 rounded-md text-xs font-semibold transition-colors ${
                  p === currentPage
                    ? "bg-slate-900 text-white border border-slate-900"
                    : "bg-white text-slate-700 border border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                }`}
              >
                {p}
              </button>
            ),
          )}
          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="w-8 h-8 flex items-center justify-center rounded-md border border-slate-200 text-slate-600 hover:border-slate-300 hover:text-slate-900 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            aria-label="Next page"
          >
            <ChevronRight size={14} />
          </button>
        </nav>
      )}
    </div>
  );
};

export default Pagination;
